import React, { useState, useEffect, useMemo } from 'react';
import { ChartDataService } from '../../services/ChartDataService';
import { useUnifiedAuth } from '../../auth/useUnifiedAuth';
import { useSAPDataStore } from '../../store/SAPDataStore';
import { useChartCache } from '../../hooks/useChartCache';
import { useThemedCharts } from '../../hooks/useThemedCharts';
import BaseStockChart from './BaseStockChart';
import { 
  usePresetMarkers, 
  generatePresetPlotLines, 
  generatePresetChartEvents, 
  enhanceTooltipWithPresets,
  PresetToggle
} from './PresetMarkers';

interface RejectRatioDataPoint {
  date: string;
  ratio: number;
  shift: string;
}

interface RejectRatioResponse {
  status: string;
  results: {
    reject_ratio: Record<string, Record<string, number>>;
    avg_reject_rate: number;
  };
}

interface RejectRatioChartProps {
  machineSerialNo: string;
  lineId?: string;
  shifts: string[];
  className?: string;
}

const RejectRatioChart: React.FC<RejectRatioChartProps> = ({ 
  machineSerialNo, 
  shifts = [],
  className = ""
}) => {
  const { token } = useUnifiedAuth();
  const { dateRange, getMachineById, getTargetForMachine } = useSAPDataStore();
  const { getStatusColors } = useThemedCharts();
  const [rawChartData, setRawChartData] = useState<RejectRatioDataPoint[]>([]);
  const [_data, setData] = useState<RejectRatioResponse['results'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Preset markers functionality
  const [showPresetMarkers, setShowPresetMarkers] = useState(false);
  const { presetData } = usePresetMarkers(machineSerialNo, showPresetMarkers, shifts);

  // Use chart cache for better performance
  const chartCache = useChartCache<RejectRatioResponse['results']>({
    maxAge: 5 * 60 * 1000, // 5 minutes
    maxEntries: 30 // Cache up to 30 different chart configurations
  });

  // Calculate dynamic target for the current machine
  const dynamicTarget = useMemo(() => {
    if (!machineSerialNo) return null;
    
    const machine = getMachineById(machineSerialNo);
    if (!machine) return null;
    
    return getTargetForMachine(machine, 'rejectRatio');
  }, [machineSerialNo, getMachineById, getTargetForMachine]);

  // Transform data for BaseChart (similar to OutputChart and EfficiencyChart)
  const stockChartProps = useMemo(() => {
    // Group data by shift to create series
    const shiftGroups = rawChartData.reduce((acc, item) => {
      if (!acc[item.shift]) {
        acc[item.shift] = [];
      }
      acc[item.shift].push(item);
      return acc;
    }, {} as Record<string, RejectRatioDataPoint[]>);

    // Create series for each shift with timestamp data
    const series = Object.keys(shiftGroups).map((shift) => {
      const shiftData = shiftGroups[shift];
      
      // Convert to [timestamp, value] format for Highstock
      const seriesData: Array<[number, number]> = shiftData.map(item => {
        const timestamp = new Date(item.date).getTime(); // item.date is now the full timestamp
        const rawValue = item.ratio;
        // Ensure numeric value - parse string if needed
        const value = typeof rawValue === 'number' ? rawValue : (parseFloat(String(rawValue)) || 0);
        return [timestamp, value] as [number, number];
      }).filter(([ts, val]) => !isNaN(ts) && !isNaN(val)) // Filter out invalid data points
       .sort((a, b) => a[0] - b[0]); // Sort by timestamp

      return {
        name: `Shift ${shift}`,
        data: seriesData
        // Color will be automatically assigned by BaseStockChart using default color palette
      };
    });

    // Calculate average from all data points on frontend
    const allValues = rawChartData.map(d => d.ratio).filter(v => !isNaN(v) && v !== null);
    const calculatedAvg = allValues.length > 0 
      ? allValues.reduce((sum, v) => sum + v, 0) / allValues.length 
      : 0;

    return {
      series,
      title: 'Reject Ratio Over Time',
      subtitle: `${machineSerialNo || 'No machine selected'} • Average: ${calculatedAvg.toFixed(2)}% • ${series.length} shift${series.length !== 1 ? 's' : ''}`,
      yAxisTitle: 'Reject Ratio (%)',
      avgRejectRate: calculatedAvg
    };
  }, [rawChartData, machineSerialNo]);

  // Prepare plot lines with dynamic target and average
  const plotLines = useMemo(() => {
    const lines = [];
    
    // Always add average line if data exists (calculated on frontend)
    if (stockChartProps.avgRejectRate !== undefined && stockChartProps.avgRejectRate > 0) {
      lines.push({
        value: stockChartProps.avgRejectRate,
        color: getStatusColors.success,
        dashStyle: 'Dash' as const,
        width: 2,
        label: 'Average'
      });
    }
    
    // Add dynamic target line if configured
    if (dynamicTarget !== null) {
      lines.push({
        value: dynamicTarget,
        color: getStatusColors.plotLine,
        dashStyle: 'Dash' as const,
        width: 2,
        label: 'Target'
      });
    }
    
    return lines;
  }, [stockChartProps.avgRejectRate, dynamicTarget, getStatusColors.success, getStatusColors.plotLine]);

  useEffect(() => {
    fetchRejectRatioData();
  }, [machineSerialNo, dateRange.start, dateRange.end, shifts]);

  const fetchRejectRatioData = async () => {
    // Don't fetch if no shifts selected
    if (!shifts || shifts.length === 0) {
      setData(null);
      setRawChartData([]);
      setLoading(false);
      return;
    }

    if (!machineSerialNo || !dateRange.start || !dateRange.end || !token) return;

    // Check cache first
    const cachedData = chartCache.getCachedData(
      'pc.reject_ratio',
      machineSerialNo,
      dateRange,
      shifts
    );

    if (cachedData) {
      setData(cachedData);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = ChartDataService.buildRequestParams(machineSerialNo, dateRange, shifts);
      const response = await ChartDataService.fetchRejectRatio(params, token);

      if (response.success && response.data) {
        const responseData = response.data as any;
        
        console.log('📊 RejectRatioChart: Raw API response:', {
          success: response.success,
          responseKeys: Object.keys(responseData),
          data: responseData
        });
        
        // Backend returns { status, results, error } - extract results
        const results = responseData.results || responseData;
        
        console.log('📊 RejectRatioChart: Extracted results:', {
          resultsKeys: results ? Object.keys(results) : 'null',
          hasRejectRatio: !!results?.reject_ratio,
          sampleShifts: results?.reject_ratio ? Object.keys(results.reject_ratio) : Object.keys(results).filter(k => !['status', 'error'].includes(k)).slice(0, 5)
        });
        
        setData(results);
        chartCache.setCachedData('pc.reject_ratio', machineSerialNo, dateRange, shifts, results);
        
        // Process response data similar to OutputChart
        const processedData: RejectRatioDataPoint[] = [];
        
        // Handle flat array format: [{ date: '...', ratio: 0.5, shift: 'A1' }, ...]
        if (Array.isArray(results)) {
          console.log('📊 RejectRatioChart: Handling flat array format, length:', results.length);
          results.forEach((item: any) => {
            if (item.ratio !== undefined || item.reject_ratio !== undefined || item.value !== undefined) {
              const rawRatio = item.ratio ?? item.reject_ratio ?? item.value ?? 0;
              const ratio = typeof rawRatio === 'number' ? rawRatio : parseFloat(rawRatio) || 0;
              const date = item.date || item.timestamp || '';
              const shift = item.shift || 'Unknown';
              
              if (date && (shifts.length === 0 || shifts.includes(shift))) {
                const cleanDate = date.replace(' +0000 UTC', '').replace(' UTC', '');
                processedData.push({ date: cleanDate, ratio, shift });
              }
            }
          });
        }
        // Check if data has reject_ratio key or is directly shift-keyed (object format)
        else {
          const rejectData = results.reject_ratio || results;
          
          if (rejectData && typeof rejectData === 'object') {
            // Iterate through shifts
            Object.keys(rejectData).forEach(shift => {
              // Skip non-shift keys
              if (shift === 'status' || shift === 'error' || shift === 'avg_reject_rate') return;
              
              const shiftData = rejectData[shift];
              if (typeof shiftData !== 'object') return;
              
              // Only include if in selected shifts or no shifts filter
              if (shifts.length === 0 || shifts.includes(shift)) {
                Object.keys(shiftData).forEach(timestamp => {
                  // Handle timestamp format like "2016-05-22 07:30:00 +0000 UTC"
                  const cleanTimestamp = timestamp.replace(' +0000 UTC', '').replace(' UTC', '');
                  const rawRatio = shiftData[timestamp] || 0;
                  const ratio = typeof rawRatio === 'number' ? rawRatio : parseFloat(rawRatio) || 0;
                  
                  processedData.push({
                    date: cleanTimestamp, // Keep more precise timestamp
                    ratio,
                    shift
                  });
                });
              }
            });
          }
        }

        // Sort by timestamp (string comparison works for ISO timestamps)
        processedData.sort((a, b) => a.date.localeCompare(b.date));
        
        console.log('📊 RejectRatioChart: Processed data:', {
          processedDataLength: processedData.length,
          sampleItems: processedData.slice(0, 3)
        });
        
        setRawChartData(processedData);
      } else {
        setError('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching reject ratio data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch reject ratio data');
    } finally {
      setLoading(false);
    }
  };

  // Show loading animation first
  if (loading) {
    return (
      <div className={`w-full ${className}`}>
        <BaseStockChart
          type="column"
          height={500}
          series={[]}
          loading={true}
        />
      </div>
    );
  }

  // Check if we have processed chart data
  if (rawChartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-lg font-semibold theme-text-primary mb-2">No Reject Ratio Data</h3>
          <p className="theme-text-secondary">No reject ratio data found for the selected period.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      <BaseStockChart
        commentProps={{ chartType: 'reject_ratio', machineBK: machineSerialNo }}
        type="column"
        height={500}
        title={stockChartProps.title}
        subtitle={stockChartProps.subtitle}
        series={stockChartProps.series}
        yAxis={{
          min: 0,
          format: '{value}%',
          title: stockChartProps.yAxisTitle,
          plotLines: plotLines
        }}
        customOptions={{
          chart: {
            events: generatePresetChartEvents()
          },
          xAxis: {
            plotLines: generatePresetPlotLines(presetData, showPresetMarkers)
          },
          tooltip: {
            formatter: enhanceTooltipWithPresets(
              function(this: any) {
                let tooltip = `<b>${new Date(this.x).toLocaleDateString()}</b><br/>`;
                
                // Add reject ratio data
                if (this.points && this.points.length > 0) {
                  this.points.forEach((point: any) => {
                    tooltip += `<span style="color:${point.series.color}">●</span> ${point.series.name}: <b>${point.y.toFixed(2)}%</b><br/>`;
                  });
                }
                
                return tooltip;
              },
              presetData,
              showPresetMarkers
            )
          }
        }}
        tooltip={{
          suffix: '%'
        }}
        legend={{
          enabled: true,
          position: 'bottom'
        }}
        loading={loading}
        error={error}
        onRetry={fetchRejectRatioData}
        panZoom={{
          enabled: true,
          type: 'x',
          mouseWheel: true,
          pinchZoom: true
        }}
        navigator={{
          enabled: true,
          height: 80,
          // Navigator colors will be automatically themed by BaseStockChart
        }}
        rangeSelector={{
          enabled: false
        }}
        onRangeChange={(min, max) => {
          console.log('Range changed:', { 
            from: new Date(min).toISOString(), 
            to: new Date(max).toISOString() 
          });
        }}
        noDataMessage={
          !machineSerialNo ? 'No machine selected' : 
          !dateRange.start || !dateRange.end ? 'Please select a date range' :
          !shifts || shifts.length === 0 ? 'No shifts selected or available for the selected timeframe' :
          'No reject ratio data found for the selected period'
        }
        extraControls={
          <PresetToggle
            enabled={showPresetMarkers}
            onToggle={setShowPresetMarkers}
            presetCount={presetData.length}
          />
        }
      />
    </div>
  );
};

export default RejectRatioChart;
